import React from "react";
import AnimateHeight from "react-animate-height";
import { of, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, map, switchMap } from "rxjs/operators";
import ConfigProps from "../constants/configProps";
import NavigationTokens from "../constants/navigationTokens";
import { IAddressBudget } from "../interfaces/IAddressBudget";
import { IDisplayState } from "../interfaces/IDisplayState";
import { IMegaMenuValue } from "../interfaces/IMegaMenuValue";
import { IShoppingLocation } from "../interfaces/IShoppingLocation";
import { ITabSetCategory } from "../interfaces/ITabSetCategory";
import { ITabSetCategoriesAndHtml } from "../interfaces/ITabSetCategoryAndHtml";
import { IWorkflow } from "../interfaces/IWorkflow";
import ConfigPropService from "../utilities/configPropService";
import EnvironmentService from "../utilities/environmentService";
import NavigationTokensService from "../utilities/navigationTokensService";
import ScrollBarService from "../utilities/scrollBarService";
import { TabSetsService } from "../utilities/tabSetsService";
import UserService from "../utilities/userService";
import { IBrandOrService } from "./brandsAndServices/brandOrService";
import BrandsAndServices from "./brandsAndServices/brandsAndServices";
import Icon from "./icon/icon";
import InkAndTonerFinder from "./inkAndTonerFinder/inkAndTonerFinder";
import MegaMenuTemplate from "./megaMenu/megaMenuTemplate";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../utilities/baseHostUrlService";

import CategorySlider from "./categorySlider/categorySlider";
import AddressSelectWrapper from "./addressSelect/indexWrapper";
import ServerSideService from "../utilities/serverSideService";

const MegaMenuModes = {
  ShopByCategory: 0,
  MoreBrandsAndServices: 1,
};

let _latestMegaMenuMode: number | null = null;

export interface IHeaderBottomProps {
  displayState: IDisplayState;
  myListsUrl?: string;
  setMyListsOpen: (value: boolean) => void;
  brandsAndServices: IBrandOrService[];
  categories: ITabSetCategory[];
  isLoggedIn: boolean;
  isCookied: boolean;
  shoppingLocation?: IShoppingLocation;
  setShoppingLocation: (shoppingLocation: IShoppingLocation) => void;
  workflow: IWorkflow;
  budgetCallback: (addressBudget: IAddressBudget) => void;
  defaultSortOrderSearch: string;
}

export interface IHeaderBottomState {
  megaMenuOpen: boolean;
  megaMenuMode: number;
  megaMenuCategory: ITabSetCategory | null;
  displayMegaMenuCategories: boolean;
  megaMenuHtml?: string;
  barHasMouse: boolean;
  navTokens: Record<string, string>;
  megaMenuCategories?: ITabSetCategory[];
  hoveredCategory: ITabSetCategory | null;
  clickedExit: boolean;
  megaMenuRequestTime: number;
  megaMenuChild: React.ReactNode;
  configProps: Record<string, string>;
  shopUrl: string;
  quickOrderUrl: string;
  isEcom: boolean;
}

class HeaderBottom extends React.Component<
  IHeaderBottomProps,
  IHeaderBottomState
> {
  categorySubject = new Subject<ITabSetCategory | null>();

  constructor(props: IHeaderBottomProps) {
    super(props);
    this.state = {
      megaMenuOpen: false,
      megaMenuMode: MegaMenuModes.ShopByCategory,
      megaMenuCategory: null,
      displayMegaMenuCategories: false,
      barHasMouse: false,
      navTokens: {},
      hoveredCategory: null,
      clickedExit: false,
      megaMenuRequestTime: 0,
      megaMenuChild: null,
      configProps: {},
      shopUrl: WBMUrlService.getShopUrl(
        BaseHostUrlService.get(),
        EnvironmentService.get().ecomSearch!
      ),
      quickOrderUrl: WBMUrlService.getQuickOrderUrl(
        BaseHostUrlService.get(),
        EnvironmentService.get().ecomQuickOrder!
      ),
      isEcom: EnvironmentService.get().isEcom === true,
    };

    this.setMegaMenuOpen = this.setMegaMenuOpen.bind(this);
    this.isShopByCategoryOpen = this.isShopByCategoryOpen.bind(this);
    this.setBarHasMouse = this.setBarHasMouse.bind(this);
    this.onCategoryClick = this.onCategoryClick.bind(this);
    this.closeMegaMenu = this.closeMegaMenu.bind(this);
    this.forceCloseMegaMenu = this.forceCloseMegaMenu.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.setGrayBarRadius = this.setGrayBarRadius.bind(this);
  }

  private setMegaMenuOpenTimeout: number | undefined;

  setMegaMenuOpen(value: boolean, mode: number | null = null) {
    const newState: Partial<IHeaderBottomState> = {
      megaMenuOpen: value,
      megaMenuMode: mode != null ? mode : this.state.megaMenuMode,
      barHasMouse: true,
      megaMenuCategory: null,
      displayMegaMenuCategories: false,
      megaMenuRequestTime: 0,
      clickedExit: !value,
    };

    _latestMegaMenuMode = newState.megaMenuMode as number;
    ScrollBarService.applyModalOpen(
      value && _latestMegaMenuMode !== MegaMenuModes.ShopByCategory,
      "mega menu",
      mode === MegaMenuModes.MoreBrandsAndServices
    );

    this.setGrayBarRadius(
      value
        ? true
        : (document.getElementById("header-bottom") as HTMLElement).style
            .borderBottomRightRadius !== "20px"
    );

    if (value === false || value !== this.state.megaMenuOpen) {
      this.categorySubject.next(null);
    } else if (mode !== this.state.megaMenuMode) {
      this.categorySubject.next(null);
    }

    if (
      this.state.megaMenuOpen &&
      value &&
      mode != null &&
      mode !== this.state.megaMenuMode
    ) {
      this.setState(
        {
          megaMenuOpen: false,
        },
        () => {
          this.setGrayBarRadius(true);

          if (this.setMegaMenuOpenTimeout) {
            clearTimeout(this.setMegaMenuOpenTimeout);
          }
          this.setMegaMenuOpenTimeout = setTimeout(() => {
            this.setState(newState as IHeaderBottomState);
          }, 200) as unknown as number;
        }
      );
    } else {
      this.setState(newState as IHeaderBottomState);
    }
  }

  isShopByCategoryOpen() {
    return (
      this.state.megaMenuMode === MegaMenuModes.ShopByCategory &&
      this.state.megaMenuOpen
    );
  }

  isMoreBrandsAndServicesOpen() {
    return (
      this.state.megaMenuMode === MegaMenuModes.MoreBrandsAndServices &&
      this.state.megaMenuOpen
    );
  }

  setGrayBarRadius(value: boolean) {
    const grayBar = document.getElementById("header-bottom") as HTMLElement;

    if (value) {
      grayBar.style.borderBottomLeftRadius = "0px";
      grayBar.style.borderBottomRightRadius = "0px";
    } else {
      grayBar.style.borderBottomLeftRadius = "20px";
      grayBar.style.borderBottomRightRadius = "20px";
    }
  }

  setBarHasMouse(value: boolean, callback = undefined) {
    this.setState(
      {
        barHasMouse: value,
      },
      callback
    );
  }

  private categoryHoverId: string | undefined | null;
  private categoryHoverTimer: number | undefined | null;

  onCategoryClick(category: ITabSetCategory) {
    if (
      this.state.megaMenuCategory &&
      this.state.megaMenuCategory.id === category.id
    ) {
      return;
    }

    this.categoryHoverId = category.id;
    if (this.categoryHoverTimer) {
      clearTimeout(this.categoryHoverTimer);
    }

    this.setState({
      hoveredCategory: category,
    });

    this.categoryHoverTimer = setTimeout(() => {
      this.categorySubject.next(category);
      this.categoryHoverId = null;
      this.categoryHoverTimer = null;
    }, 300) as unknown as number;
  }

  onMouseLeave(category: ITabSetCategory) {
    if (category.id === this.categoryHoverId) {
      if (this.categoryHoverTimer) {
        clearTimeout(this.categoryHoverTimer);
        this.categoryHoverTimer = null;
      }
    }
  }

  checkShouldCloseMegaMenu() {
    if (this.state.megaMenuOpen) {
      if (this.state.barHasMouse) {
        return;
      }

      this.setMegaMenuOpen(false);
    }
  }

  getMegaMenuChild(value: IMegaMenuValue) {
    if (value.category.inkAndToner) {
      return <InkAndTonerFinder />;
    }

    return null;
  }

  private navTokenSub: Subscription | undefined;
  private configPropsSub: Subscription | undefined;
  private categorySub: Subscription | undefined;
  private categoryTimeout: number | undefined;

  componentDidMount() {
    if (typeof document !== undefined) {
      document.addEventListener("click", this.closeMegaMenu);
      document.addEventListener("closeMegaMenu", this.forceCloseMegaMenu);
      this.setGrayBarRadius(false);
    }

    this.navTokenSub = NavigationTokensService.get([
      NavigationTokens.quickOrder,
      NavigationTokens.myWbRecentItems,
    ]).subscribe((navTokens) => {
      this.setState({
        navTokens: navTokens,
      });
    });

    this.configPropsSub = ConfigPropService.get([
      ConfigProps.DefaultSortOrderSearch,
      ConfigProps.EnableResponsiveSearchResults,
      ConfigProps.EnableResponsiveShopByItemNumber,
    ]).subscribe((configProps: Record<string, string>) => {
      this.setState({
        configProps: configProps,
      });
    });

    this.categorySub = this.categorySubject
      .asObservable()
      .pipe(distinctUntilChanged())
      .pipe(
        switchMap((value) => {
          if (!value || !value.id) {
            document.dispatchEvent(
              new CustomEvent("megaMenuOpened", { detail: { opened: false } })
            );

            return of(null);
          }
          this.setState({
            megaMenuRequestTime: new Date().getTime(),
          });
          return TabSetsService.getSubCategories(value.id).pipe(
            map((c: ITabSetCategoriesAndHtml) => {
              return {
                categories: c.categories,
                html: c.html,
                category: value,
              };
            })
          );
        })
      )
      .subscribe((value: unknown) => {
        if (!value) {
          this.setState(
            {
              megaMenuCategory: null,
              displayMegaMenuCategories: false,
            },
            () => {
              if (_latestMegaMenuMode === MegaMenuModes.ShopByCategory) {
                ScrollBarService.applyModalOpen(false, "mega menu");
              }
            }
          );
          return;
        }
        this.setState(
          {
            megaMenuCategory: (value as IMegaMenuValue).category,
            displayMegaMenuCategories: false,
          },
          () => {
            // time it with the animation, delay up to 200ms
            // substract the time it took to make the network call
            const currentTime = new Date().getTime();
            if (this.categoryTimeout) {
              clearTimeout(this.categoryTimeout);
            }

            const maxAnimationTime = 200;
            const minAnimationTime = 100;

            this.categoryTimeout = setTimeout(
              () => {
                this.setState({
                  megaMenuCategories: (value as ITabSetCategoriesAndHtml)
                    .categories,
                  megaMenuHtml: (value as ITabSetCategoriesAndHtml).html,
                  displayMegaMenuCategories: true,
                  megaMenuChild: this.getMegaMenuChild(value as IMegaMenuValue),
                });
                if (
                  (((value as ITabSetCategoriesAndHtml).categories &&
                    (value as ITabSetCategoriesAndHtml).categories.length !==
                      0) ||
                    (value as ITabSetCategoriesAndHtml).html) &&
                  _latestMegaMenuMode === MegaMenuModes.ShopByCategory
                ) {
                  ScrollBarService.applyModalOpen(true, "mega menu");
                }
              },
              !this.state.megaMenuHtml && !this.state.megaMenuCategory
                ? 0
                : Math.min(
                    maxAnimationTime,
                    Math.max(
                      minAnimationTime,
                      maxAnimationTime -
                        (currentTime - this.state.megaMenuRequestTime)
                    )
                  )
            ) as unknown as number;
          }
        );
      });
  }

  closeMegaMenu() {
    if (!this.state.barHasMouse && this.state.megaMenuOpen) {
      this.setMegaMenuOpen(false);
    }
  }

  forceCloseMegaMenu() {
    if (this.state.megaMenuOpen) {
      this.setMegaMenuOpen(false);
    }
  }

  private animateTimeout: number | undefined;

  componentWillUnmount() {
    if (typeof document !== undefined) {
      document.removeEventListener("click", this.closeMegaMenu);
      document.removeEventListener("closeMegaMenu", this.forceCloseMegaMenu);
    }

    if (this.navTokenSub) {
      this.navTokenSub.unsubscribe();
    }

    if (this.configPropsSub) {
      this.configPropsSub.unsubscribe();
    }

    if (this.categorySubject) {
      this.categorySubject.complete();
    }

    if (this.categorySub) {
      this.categorySub.unsubscribe();
    }

    if (this.animateTimeout) {
      clearTimeout(this.animateTimeout);
    }

    if (this.categoryTimeout) {
      clearTimeout(this.categoryTimeout);
    }

    if (this.setMegaMenuOpenTimeout) {
      clearTimeout(this.setMegaMenuOpenTimeout);
    }
  }

  recentlyViewedItemsLink() {
    if (
      this.state.navTokens &&
      this.state.navTokens[NavigationTokens.myWbRecentItems] &&
      this.state.configProps
    ) {
      if (
        ConfigPropService.isYesOrTrue(
          this.state.configProps[ConfigProps.EnableResponsiveSearchResults]
        )
      ) {
        return `${this.state.shopUrl}?priceplan=userviewed&rvi=1`;
      } else {
        const sortCode =
          this.state.configProps[ConfigProps.DefaultSortOrderSearch];

        return `${
          this.state.navTokens[NavigationTokens.myWbRecentItems]
        }?rvi=1&sc=${sortCode}&fi=1&fr=1`;
      }
    }

    return undefined;
  }

  orderAgainLink() {
    if (
      this.state.navTokens &&
      this.state.navTokens[NavigationTokens.myWbRecentItems] &&
      this.state.configProps
    ) {
      if (
        ConfigPropService.isYesOrTrue(
          this.state.configProps[ConfigProps.EnableResponsiveSearchResults]
        )
      ) {
        return `${this.state.shopUrl}?fi=1&fr=1&oa=1&priceplan=userordered&sc=or`;
      } else {
        return `${
          this.state.navTokens[NavigationTokens.myWbRecentItems]
        }?sc=OR&fi=1&fr=1`;
      }
    }

    return undefined;
  }

  quickOrderLink() {
    if (
      this.state.navTokens &&
      this.state.navTokens[NavigationTokens.quickOrder] &&
      this.state.configProps
    ) {
      if (
        ConfigPropService.isYesOrTrue(
          this.state.configProps[ConfigProps.EnableResponsiveShopByItemNumber]
        )
      ) {
        return this.state.quickOrderUrl;
      } else {
        return this.state.navTokens[NavigationTokens.quickOrder];
      }
    }

    return undefined;
  }

  render() {
    return (
      <>
        <div
          className="header-bottom row d-lg-block hidden-background"
          id="header-bottom"
          style={
            ServerSideService.isServerSide
              ? { borderBottomLeftRadius: 20, borderBottomRightRadius: 20 }
              : {}
          }
        ></div>
        <div
          className="header-bottom row d-block"
          onMouseEnter={() => {
            this.setBarHasMouse(true);
          }}
          onMouseLeave={() => {
            this.setBarHasMouse(false);
          }}
        >
          <div className="feature-items d-none d-lg-flex">
            {UserService.displayUserInfo() &&
              this.props.displayState.subTabSet && (
                <button
                  className={
                    "feature-button shop-by-category" +
                    (this.isShopByCategoryOpen() ? " f-b-selected" : "")
                  }
                  onClick={() => {
                    this.setMegaMenuOpen(
                      this.isShopByCategoryOpen()
                        ? !this.state.megaMenuOpen
                        : true,
                      MegaMenuModes.ShopByCategory
                    );
                  }}
                >
                  <Icon>{this.isShopByCategoryOpen() ? "close" : "menu"}</Icon>
                  <span>
                    <b>Shop By Category</b>
                  </span>
                </button>
              )}

            {UserService.displayUserInfo() &&
              this.props.displayState.displayQuickOrderLinks && (
                <a
                  className="feature-button"
                  onClick={() => {
                    this.setMegaMenuOpen(false);
                  }}
                  href={this.quickOrderLink()}
                >
                  <Icon>menu_book</Icon>
                  <span>Shop By Item Number</span>
                  <span className="feature-underline">
                    <span></span>
                  </span>
                </a>
              )}

            {UserService.displayUserInfo() &&
              this.props.displayState.displayEasyReorder && (
                <a
                  className="feature-button"
                  onClick={() => {
                    this.setMegaMenuOpen(false);
                  }}
                  href={this.orderAgainLink()}
                >
                  <Icon>replay</Icon>
                  <span>Order Again</span>
                  <span className="feature-underline">
                    <span></span>
                  </span>
                </a>
              )}

            {UserService.displayUserInfo() &&
              this.props.displayState.displayRecentlyViewed && (
                <a
                  className="feature-button"
                  onClick={() => {
                    this.setMegaMenuOpen(false);
                  }}
                  href={this.recentlyViewedItemsLink()}
                >
                  <Icon>history</Icon>
                  <span>Recently Viewed</span>
                  <span className="feature-underline">
                    <span></span>
                  </span>
                </a>
              )}

            {UserService.displayUserInfo() &&
              this.props.brandsAndServices.length > 0 && (
                <>
                  <span className="feature-divider"></span>

                  <button
                    className="feature-button more-brands-and-services"
                    onClick={() => {
                      this.setMegaMenuOpen(
                        this.isMoreBrandsAndServicesOpen()
                          ? !this.state.megaMenuOpen
                          : true,
                        MegaMenuModes.MoreBrandsAndServices
                      );
                    }}
                  >
                    <span>More Brands & Services</span>
                    <Icon
                      className={`animate-arrow ${
                        this.isMoreBrandsAndServicesOpen() ? "selected" : ""
                      }`}
                    >
                      keyboard_arrow_down
                    </Icon>
                    <span className="feature-underline">
                      <span></span>
                    </span>
                  </button>
                </>
              )}
          </div>
          <div
            className="d-inline-block shipping-address-header"
            onClick={() => {
              this.setMegaMenuOpen(false);
            }}
          >
            <AddressSelectWrapper
              isLoggedIn={this.props.isLoggedIn}
              isCookied={this.props.isCookied}
              shoppingLocation={this.props.shoppingLocation}
              displayState={this.props.displayState}
              setShoppingLocation={this.props.setShoppingLocation}
              workflow={this.props.workflow}
              isSecondarySlideIn={false}
              budgetCallback={this.props.budgetCallback}
              appendRemoveContainerWidth={(isOpen: boolean) => {
                ScrollBarService.applyModalOpen(isOpen, "address select", true);
              }}
              renderTrigger={(currentFormattedAddress: React.ReactNode) => (
                <div tabIndex={0}>
                  <div className="location-icon">
                    <Icon className="align-middle">location_on</Icon>
                  </div>
                  <div
                    className={`delivery-address${
                      this.state.isEcom ? " ecom" : ""
                    }`}
                  >
                    <span className="d-none d-lg-inline deliver-to">
                      Deliver to
                    </span>
                    <span className="break-line" />
                    <b className="delivery-address-text">
                      {currentFormattedAddress !== null
                        ? currentFormattedAddress
                        : "Select An Address"}
                    </b>
                  </div>
                </div>
              )}
            />
          </div>
          <AnimateHeight
            className="d-none d-lg-block w-100"
            duration={200}
            height={this.state.megaMenuOpen ? "auto" : 0}
            onAnimationStart={(obj) => {
              if (obj.newHeight === 0 && this.state.clickedExit) {
                if (this.animateTimeout) {
                  clearTimeout(this.animateTimeout);
                }
                this.animateTimeout = setTimeout(() => {
                  this.setGrayBarRadius(this.state.megaMenuOpen);
                }, 150) as unknown as number;
              }
            }}
          >
            <div className="w-100 h-100">
              {this.state.megaMenuMode === MegaMenuModes.ShopByCategory && (
                <>
                  <CategorySlider
                    onMouseEnter={this.onCategoryClick}
                    onMouseLeave={this.onMouseLeave}
                    categories={this.props.categories}
                    selectedCategory={this.state.megaMenuCategory}
                    hoveredCategory={this.state.hoveredCategory}
                  />
                  <AnimateHeight
                    className="w-100"
                    duration={200}
                    height={this.state.displayMegaMenuCategories ? "auto" : 0}
                  >
                    <div className="w-100 h-100">
                      {(this.state.megaMenuHtml ||
                        (this.state.megaMenuCategories &&
                          this.state.megaMenuCategories.length !== 0)) && (
                        <MegaMenuTemplate
                          html={this.state.megaMenuHtml}
                          categories={this.state.megaMenuCategories}
                          child={this.state.megaMenuChild}
                          megaMenuCategory={this.state.megaMenuCategory}
                          defaultSortOrderSearch={
                            this.props.defaultSortOrderSearch
                          }
                        />
                      )}
                    </div>
                  </AnimateHeight>
                </>
              )}
              {this.state.megaMenuMode ===
                MegaMenuModes.MoreBrandsAndServices && (
                <>
                  <AnimateHeight
                    className="w-100"
                    duration={200}
                    height={
                      this.state.megaMenuMode ===
                      MegaMenuModes.MoreBrandsAndServices
                        ? "auto"
                        : 0
                    }
                  >
                    <div
                      style={{
                        backgroundColor: "#eee",
                        marginTop: 40,
                        paddingLeft: 20,
                        paddingRight: 20,
                        height: "100%",
                      }}
                    >
                      <div
                        style={{
                          borderTop: "2px #333333 solid",
                        }}
                      ></div>

                      <BrandsAndServices
                        brandsAndServices={this.props.brandsAndServices}
                      />
                    </div>
                  </AnimateHeight>
                </>
              )}
            </div>
          </AnimateHeight>
          <div className="header-bottom row d-none d-lg-block slide-down-background"></div>
        </div>
      </>
    );
  }
}

export default HeaderBottom;
